import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './Tooltip.module.css';

/**
 * Tooltip component that displays immediately on hover
 * @param {Object} props
 * @param {React.ReactNode} props.children - The element to attach the tooltip to
 * @param {string} props.text - The tooltip text to display
 * @param {string} props.position - Position: 'top', 'bottom', 'left', 'right' (default: 'top')
 * @param {string} props.className - Additional CSS class names
 * @param {boolean} props.forceVisible - If true, force tooltip to be visible (for controlled mode, e.g. mobile)
 */
function Tooltip({ children, text, position = 'top', className = '', forceVisible = false }) {
    const [isVisible, setIsVisible] = useState(false);
    const [tooltipStyle, setTooltipStyle] = useState({});
    const wrapperRef = useRef(null);
    const tooltipRef = useRef(null);
    
    // Use forceVisible if provided, otherwise use internal hover state
    const shouldShow = forceVisible || isVisible;

    useEffect(() => {
        if (shouldShow && wrapperRef.current && tooltipRef.current) {
            const updatePosition = () => {
                if (!wrapperRef.current || !tooltipRef.current) return;
                
                const wrapperRect = wrapperRef.current.getBoundingClientRect();
                const tooltipRect = tooltipRef.current.getBoundingClientRect();
                const spacing = 8;
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;

                let top = 0;
                let left = 0;

                switch (position) {
                    case 'top':
                        top = wrapperRect.top - tooltipRect.height - spacing;
                        left = wrapperRect.left + (wrapperRect.width - tooltipRect.width) / 2;
                        break;
                    case 'bottom':
                        top = wrapperRect.bottom + spacing;
                        left = wrapperRect.left + (wrapperRect.width - tooltipRect.width) / 2;
                        break;
                    case 'left':
                        top = wrapperRect.top + (wrapperRect.height - tooltipRect.height) / 2;
                        left = wrapperRect.left - tooltipRect.width - spacing;
                        break;
                    case 'right':
                        top = wrapperRect.top + (wrapperRect.height - tooltipRect.height) / 2;
                        left = wrapperRect.right + spacing;
                        break;
                    default:
                        top = wrapperRect.top - tooltipRect.height - spacing;
                        left = wrapperRect.left + (wrapperRect.width - tooltipRect.width) / 2;
                }

                // Keep tooltip within viewport
                if (left < 8) left = 8;
                if (left + tooltipRect.width > viewportWidth - 8) {
                    left = viewportWidth - tooltipRect.width - 8;
                }
                if (top < 8) top = 8;
                if (top + tooltipRect.height > viewportHeight - 8) {
                    top = viewportHeight - tooltipRect.height - 8;
                }

                setTooltipStyle({
                    position: 'fixed',
                    top: `${top}px`,
                    left: `${left}px`,
                    zIndex: 10000,
                });
            };

            // Use double RAF to ensure tooltip is rendered and measured
            requestAnimationFrame(() => {
                requestAnimationFrame(updatePosition);
            });

            // Update position on scroll/resize
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);

            return () => {
                window.removeEventListener('scroll', updatePosition, true);
                window.removeEventListener('resize', updatePosition);
            };
        }
    }, [shouldShow, position]);

    const handleMouseEnter = () => {
        setIsVisible(true);
    };

    const handleMouseLeave = () => {
        setIsVisible(false);
    };

    if (!text) {
        return children;
    }

    const tooltipElement = shouldShow ? (
        <span
            ref={tooltipRef}
            className={`${styles.tooltip} ${styles[position]}`}
            style={tooltipStyle}
        >
            {text}
        </span>
    ) : null;

    return (
        <>
            <span
                ref={wrapperRef}
                className={`${styles.tooltipWrapper} ${className}`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {children}
            </span>
            {tooltipElement && typeof document !== 'undefined' && createPortal(tooltipElement, document.body)}
        </>
    );
}

export default Tooltip;
